import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { Site, Zone, Address, GeoPoint } from '@easydone/shared';

/**
 * Get all sites for an organization
 */
export async function getSites(orgId: string): Promise<Site[]> {
  const sitesRef = collection(db, 'organizations', orgId, 'sites');
  const q = query(sitesRef, orderBy('name', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Site));
}

/**
 * Get a single site by ID
 */
export async function getSite(orgId: string, siteId: string): Promise<Site | null> {
  const siteRef = doc(db, 'organizations', orgId, 'sites', siteId);
  const snapshot = await getDoc(siteRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Site;
}

/**
 * Site creation data (without id and timestamps)
 */
export interface CreateSiteData {
  name: string;
  code: string;
  type: string;
  address: Address;
  location?: GeoPoint;
  description?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  assignedUsers?: string[];
  metadata?: Record<string, any>;
}

/**
 * Create a new site
 */
export async function createSite(orgId: string, data: CreateSiteData): Promise<Site> {
  const sitesRef = collection(db, 'organizations', orgId, 'sites');

  const siteData = {
    ...data,
    orgId,
    status: 'active',
    zones: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(sitesRef, siteData);
  const snapshot = await getDoc(docRef);

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Site;
}

/**
 * Update a site
 */
export async function updateSite(
  orgId: string,
  siteId: string,
  data: Partial<CreateSiteData>
): Promise<void> {
  const siteRef = doc(db, 'organizations', orgId, 'sites', siteId);
  await updateDoc(siteRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a site
 */
export async function deleteSite(orgId: string, siteId: string): Promise<void> {
  const siteRef = doc(db, 'organizations', orgId, 'sites', siteId);
  await deleteDoc(siteRef);
}

/**
 * Zone creation data
 */
export interface CreateZoneData {
  name: string;
  code: string;
  type?: string;
  description?: string;
  location?: GeoPoint;
  metadata?: Record<string, any>;
}

/**
 * Add a zone to a site
 */
export async function addZone(
  orgId: string,
  siteId: string,
  zoneData: CreateZoneData
): Promise<void> {
  const siteRef = doc(db, 'organizations', orgId, 'sites', siteId);
  const siteSnapshot = await getDoc(siteRef);

  if (!siteSnapshot.exists()) {
    throw new Error('Site not found');
  }

  const site = siteSnapshot.data() as Site;
  const zones = site.zones || [];

  const newZone: Zone = {
    id: `zone_${Date.now()}`,
    siteId,
    ...zoneData,
    createdAt: Timestamp.now(),
  };

  zones.push(newZone);

  await updateDoc(siteRef, {
    zones,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update a zone within a site
 */
export async function updateZone(
  orgId: string,
  siteId: string,
  zoneId: string,
  zoneData: Partial<CreateZoneData>
): Promise<void> {
  const siteRef = doc(db, 'organizations', orgId, 'sites', siteId);
  const siteSnapshot = await getDoc(siteRef);

  if (!siteSnapshot.exists()) {
    throw new Error('Site not found');
  }

  const site = siteSnapshot.data() as Site;
  const zones = site.zones || [];

  const zoneIndex = zones.findIndex(z => z.id === zoneId);
  if (zoneIndex === -1) {
    throw new Error('Zone not found');
  }

  zones[zoneIndex] = {
    ...zones[zoneIndex],
    ...zoneData,
  };

  await updateDoc(siteRef, {
    zones,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a zone from a site
 */
export async function deleteZone(
  orgId: string,
  siteId: string,
  zoneId: string
): Promise<void> {
  const siteRef = doc(db, 'organizations', orgId, 'sites', siteId);
  const siteSnapshot = await getDoc(siteRef);

  if (!siteSnapshot.exists()) {
    throw new Error('Site not found');
  }

  const site = siteSnapshot.data() as Site;
  const zones = (site.zones || []).filter(z => z.id !== zoneId);

  await updateDoc(siteRef, {
    zones,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get sites assigned to a specific user
 */
export async function getUserSites(orgId: string, userId: string): Promise<Site[]> {
  const sitesRef = collection(db, 'organizations', orgId, 'sites');
  const q = query(
    sitesRef,
    where('assignedUsers', 'array-contains', userId),
    orderBy('name', 'asc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Site));
}
